// Implements: F-CNV-016, F-UIX-022, F-UIX-008, F-UIX-009, F-UIX-010
/**
 * The brief the model wrote for a turn the person asked to review (OD-8), editable, with the
 * action that draws from it. Pure presentation.
 *
 * F-UIX-022: a brief the model ordered into sections is shown as one labelled field per section,
 * grouped under the brief's name and its hint; a brief with no recognisable sections is one field,
 * as it always was. The values are props so a refused confirmation keeps every field as typed
 * (F-UIX-008). It is model text: it goes into textareas as values, never as markup. The refusal is
 * the server's own sentence for the codes it writes for people (busy, base version moved, brief no
 * longer pending, invalid brief). Ctrl+Enter in any field confirms. Nothing here says whether
 * drawing costs anything (OD-12).
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Textarea } from "../../../shared/ui/atoms/Textarea";
import { Field, describedBy, errorId, hintId } from "../../../shared/ui/molecules/Field";
import { TURN_REFUSAL_CODES, userMessage } from "../../../shared/ui/molecules/userMessage";
import type { ApiFailure } from "../../../types/errors";
import type { BriefPart, BriefPartKey } from "../model/briefSections";
import styles from "./BriefEditor.module.css";

/** The first field's id: one brief is under review at a time, and the focus goes there. */
export const BRIEF_FIELD_ID = "conversation-brief";

/** The label of each field; a `whole` brief is labelled by the brief's own name. */
const LABELS = {
  whole: "conversation.brief.label",
  lead: "conversation.brief.section.lead",
  screen: "conversation.brief.section.screen",
  audience: "conversation.brief.section.audience",
  content: "conversation.brief.section.content",
  actions: "conversation.brief.section.actions",
  states: "conversation.brief.section.states",
  outOfScope: "conversation.brief.section.outOfScope",
} as const satisfies Record<BriefPartKey, string>;

export interface BriefEditorProps {
  /** The brief as read once for its turn: what each field is. */
  readonly parts: readonly BriefPart[];
  /** What each part holds now, in the order of `parts`. */
  readonly values: readonly string[];
  readonly onValueChange: (index: number, value: string) => void;
  readonly onSubmit: () => void;
  /** A turn is open: nothing can be confirmed until it ends. */
  readonly disabled: boolean;
  /** The refusal of the last attempt, if any. */
  readonly error: ApiFailure | null;
}

interface Shown {
  readonly part: BriefPart;
  /** The part's place in `parts` (and in `values`). */
  readonly index: number;
}

/** The parts a person sees: a lead with no words of its own stays in the text, not on screen. */
function shownParts(parts: readonly BriefPart[]): readonly Shown[] {
  return parts
    .map((part, index) => ({ part, index }))
    .filter(({ part }) => part.key !== "lead" || part.value !== "");
}

export function BriefEditor({
  parts,
  values,
  onValueChange,
  onSubmit,
  disabled,
  error,
}: BriefEditorProps): ReactElement {
  const { t } = useTranslation();
  const blocked = disabled || values.every((value) => value.trim() === "");
  const confirm = (): void => {
    if (!blocked) onSubmit();
  };
  const hint = t("conversation.brief.hint");
  const refusal =
    error === null ? undefined : userMessage(error, t("conversation.brief.failed"), TURN_REFUSAL_CODES);
  const description = describedBy(BRIEF_FIELD_ID, { hint, error: refusal });
  const whole = parts.length === 1 && parts[0]?.key === "whole";

  const field = ({ part, index }: Shown, id: string): ReactElement => (
    <Textarea
      id={id}
      value={values[index] ?? part.value}
      onChange={(value) => {
        onValueChange(index, value);
      }}
      onSubmitShortcut={confirm}
      disabled={disabled}
      invalid={refusal !== undefined}
      describedBy={description}
    />
  );

  return (
    <form
      className={styles.form}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        confirm();
      }}
    >
      {whole ? (
        <Field id={BRIEF_FIELD_ID} label={t(LABELS.whole)} hint={hint} error={refusal}>
          {field({ part: parts[0] ?? { key: "whole", before: "", value: "", after: "" }, index: 0 }, BRIEF_FIELD_ID)}
        </Field>
      ) : (
        <fieldset className={styles.group}>
          <legend className={styles.legend}>{t(LABELS.whole)}</legend>
          <p id={hintId(BRIEF_FIELD_ID)} className={styles.hint}>
            {hint}
          </p>
          {shownParts(parts).map((shown, order) => {
            const id = order === 0 ? BRIEF_FIELD_ID : `${BRIEF_FIELD_ID}-${String(shown.index)}`;
            return (
              <Field key={id} id={id} label={t(LABELS[shown.part.key])}>
                {field(shown, id)}
              </Field>
            );
          })}
          {refusal === undefined ? null : (
            <p id={errorId(BRIEF_FIELD_ID)} className={styles.error} role="alert">
              {refusal}
            </p>
          )}
        </fieldset>
      )}
      <Button variant="primary" type="submit" disabled={blocked}>
        {t("conversation.brief.submit")}
      </Button>
    </form>
  );
}
